import { defineStore } from "pinia";
import Vue from "vue";
import SUBSCRIPTION_DATA_QUERY from "@/graphql/subscription/subscriptionData.graphql";
import PRODUCT_PRICE_QUERY from "@/graphql/subscription/productPrice.graphql";
import PAYMENT_QUERY from "@/graphql/subscription/payment.graphql";
import { useUserStore } from "~/stores/user";
import { useMateCountersStore } from "~/stores/user/mateCounters";
import { useAccountSettingsStore } from "~/stores/user/accountSettings";
import { usePageDataStore } from "~/stores/user/pageData";
import { useSubscriptionSettingsStore } from "~/stores/user/subscriptionSettings";
import { useAppLayoutStatusStore } from "~/stores/user/appLayoutStatus";
import { requestInFlight, trackRequest } from "~/stores/user/inFlightRequests";

export const SUBSCRIPTION_DATA_KEYS = [
  "id",
  "hasChargeableBankcard",
  "lastPaidSubscription",
  "nonExpiredPaidSubscriptions",
];

const belongsTo = (data, me) =>
  !!data && !!me && String(data.id) === String(me.id);

/**
 * Whether the person can have subscriptions: a professional, not a customer
 * or a logged-out visitor.
 * @param {object | null} me
 * @returns {boolean}
 */
export const needsSubscriptionData = (me) => !!me && !me.isCustomer;

const pickSubscriptionData = (person) =>
  Object.fromEntries(SUBSCRIPTION_DATA_KEYS.map((key) => [key, person[key]]));

/**
 * The logged-in professional's subscriptions, kept out of `me`
 * (`graphql/subscription/*.graphql`):
 * - the subscription data (`hasChargeableBankcard`, `lastPaidSubscription`,
 *   `nonExpiredPaidSubscriptions`), loaded where it is shown and refreshed by
 *   the subscription actions
 * - the product price of the subscription page, for one promo code at a time
 *
 * Data is returned only while it belongs to the logged-in person.
 * Customers and logged-out visitors never load it.
 */
export const useSubscriptionStore = defineStore("subscriptionStore", {
  state: () => ({
    loaded: null,
    pending: null,
    loadedProductPrice: null,
    pendingProductPrice: null,
  }),
  getters: {
    /**
     * The subscription data of the logged-in person, or null until it is
     * loaded.
     * @returns {object | null}
     */
    data(state) {
      return belongsTo(state.loaded, useUserStore().me) ? state.loaded : null;
    },
    isLoaded() {
      return !!this.data;
    },
    hasChargeableBankcard() {
      return !!this.data?.hasChargeableBankcard;
    },
    lastPaidSubscription() {
      return this.data?.lastPaidSubscription ?? null;
    },
    nonExpiredPaidSubscriptions() {
      return this.data?.nonExpiredPaidSubscriptions ?? [];
    },
    hasPaidSubscription() {
      return this.nonExpiredPaidSubscriptions.length > 0;
    },
    lastNonExpiredPaidSubscription() {
      if (!this.hasPaidSubscription) {
        return null;
      }
      return Array.from(this.nonExpiredPaidSubscriptions).sort(
        (a, b) => parseInt(b.id) - parseInt(a.id)
      )[0];
    },
    /**
     * The product price for the promo code, or null until it is loaded.
     * @returns {(promoCode?: string) => object | null}
     */
    productPrice(state) {
      return (promoCode = "") => {
        const loaded = state.loadedProductPrice;
        return belongsTo(loaded, useUserStore().me) &&
          loaded.promoCode === promoCode
          ? loaded.productPrice
          : null;
      };
    },
  },
  actions: {
    /**
     * Loads the subscription data once per person, without sending a second
     * request while one is in flight.
     * Requests in flight, also a refresh of loaded data or one that replaced
     * this call's own request, are waited for, so the call never returns with
     * data that is about to be replaced or without the data a newer request
     * brings.
     */
    async load() {
      if (!needsSubscriptionData(useUserStore().me)) {
        return;
      }
      if (!this.data && !requestInFlight(this, "data", this.pending)) {
        await this.sendQuery();
      }
      let pending = requestInFlight(this, "data", this.pending);
      while (pending) {
        await pending.catch(() => {});
        pending = requestInFlight(this, "data", this.pending);
      }
    },

    /**
     * Reloads the subscription data. A response that arrives after a newer
     * request was sent, or after `setData` / `setValues`, is dropped. A failed
     * request keeps the last data.
     */
    async refresh() {
      if (!needsSubscriptionData(useUserStore().me)) {
        return;
      }
      await this.sendQuery();
    },

    /**
     * Reloads the subscription data only if it is loaded or loading.
     */
    async refreshIfLoaded() {
      if (this.data || requestInFlight(this, "data", this.pending)) {
        await this.refresh();
      }
    },

    /**
     * Reloads what a subscription purchase, change or settled payment can
     * change: the subscription data, the person's subscription settings
     * (which also update `paymentType` and the profession and zone ids on
     * `me`), the payment and trial status, and the mate counters, the
     * settings page's billing data and the price-offer permission if they
     * are loaded.
     */
    async refreshAfterChange() {
      await Promise.all([
        this.refresh(),
        useSubscriptionSettingsStore().refresh(),
        useAppLayoutStatusStore().refresh(),
        useMateCountersStore().refreshIfLoaded(),
        useAccountSettingsStore().refreshIfLoaded(["billing", "invoices"]),
        usePageDataStore().refreshIfLoaded(["priceOffers"]),
      ]);
    },

    /**
     * Replaces the subscription data with a result that selects
     * `SubscriptionDataFragment` (a mutation's person, the payment poll).
     * @param {object | null} person
     */
    setData(person) {
      if (!belongsTo(person, useUserStore().me)) {
        return;
      }
      this.pending = null;
      this.loaded = pickSubscriptionData(person);
    },

    /**
     * Applies changed values to the loaded subscription data.
     * @param {object} values
     */
    setValues(values) {
      const data = this.data;
      if (!data) {
        return;
      }
      this.pending = null;
      for (const [key, value] of Object.entries(values)) {
        if (SUBSCRIPTION_DATA_KEYS.includes(key) && key !== "id") {
          Vue.set(data, key, value);
        }
      }
    },

    async sendQuery() {
      const userStore = useUserStore();
      const query = this.$nuxt.$query(
        SUBSCRIPTION_DATA_QUERY,
        {},
        { skipErrorPageOnAuthError: true }
      );
      const id = trackRequest(this, "data", query);
      this.pending = id;
      try {
        const result = await query;
        if (this.pending === id && belongsTo(result?.me, userStore.me)) {
          this.loaded = pickSubscriptionData(result.me);
        }
      } catch (error) {
        this.$nuxt.$logger.warn("Failed to load the subscription data", error);
      } finally {
        if (this.pending === id) {
          this.pending = null;
        }
      }
    },

    /**
     * Loads the subscription page's product price for the promo code. A call
     * made while the same promo code's request is in flight waits for it; a
     * response that arrives after a newer request was sent is dropped.
     * @param {string} [promoCode]
     */
    async loadProductPrice(promoCode = "") {
      const userStore = useUserStore();
      if (!needsSubscriptionData(userStore.me)) {
        return;
      }
      const pending = requestInFlight(
        this,
        "productPrice",
        this.pendingProductPrice?.id
      );
      if (pending && this.pendingProductPrice.promoCode === promoCode) {
        await pending.catch(() => {});
        return;
      }
      const query = this.$nuxt.$query(
        PRODUCT_PRICE_QUERY,
        { promoCode },
        { skipErrorPageOnAuthError: true }
      );
      const id = trackRequest(this, "productPrice", query);
      this.pendingProductPrice = { id, promoCode };
      try {
        const result = await query;
        if (
          this.pendingProductPrice?.id === id &&
          belongsTo(result?.me, userStore.me)
        ) {
          this.loadedProductPrice = {
            id: result.me.id,
            promoCode,
            productPrice: result.me.productPrice,
          };
        }
      } catch (error) {
        this.$nuxt.$logger.warn("Failed to load the product price", error);
      } finally {
        if (this.pendingProductPrice?.id === id) {
          this.pendingProductPrice = null;
        }
      }
    },

    /**
     * Loads a payment for the payment poll of `SubscribeMutation` and applies
     * the subscription data and the valid subscriptions (to the app layout
     * status) that come with it. It never shows the error page: a payment
     * that isn't the viewer's (another login during the poll) is FORBIDDEN, and
     * the poll only reports the failure.
     * @param {string} paymentId
     * @returns {Promise<object | null>} the payment, or null if `me` changed
     */
    async loadPayment(paymentId) {
      const userStore = useUserStore();
      const result = await this.$nuxt.$query(
        PAYMENT_QUERY,
        { paymentId },
        { skipErrorPageOnAuthError: true }
      );
      if (!belongsTo(result?.me, userStore.me)) {
        return null;
      }
      this.setData(result.me);
      useAppLayoutStatusStore().setValues({
        validSubscriptions: result.me.validSubscriptions,
      });
      return result.me.payment;
    },
  },
});
